<script setup lang="ts">
import { useWindowSize } from '@vueuse/core';
import Terms from './tournament/Terms.vue';
import TournamentTable from './tournament/TournamentTable.vue';
import RelatedGames from './tournament/RelatedGames.vue';
import { useLayout } from '~/composables/useLayout';

const { t } = useTexts();
const { params } = useRoute('vip-tournaments-tournament');

const tournamentDetails = ref<TTournamentDetailed>();

const { openToaster } = useLayoutStore();

const { loadTournamentInfo, enterInTournament } = useSmarticoTournaments();

const currentTournament = computed(() => tournamentDetails.value);

const { isDesktop } = useLayout();
const router = useRouter();

// Segue o histórico, como a lista e as Missões. O push fixo pra lista criava um
// laço: ele empilhava a lista por cima do detalhe, e o voltar da lista — que
// respeita o histórico — devolvia o detalhe. Sem histórico (deeplink de push
// notification) o destino é a lista.
const handleBackNavigation = () => {
  if (window.history.state?.back) {
    router.back();

    return;
  }

  router.push('/vip/tournaments');
};

const descriptionItems = [
  { title: 'Tipo de torneio', icon: 'tournament-single/trophy' },
  { title: 'Duração', icon: 'tournament-single/clock' },
  { title: 'Tipo de pontuação', icon: 'tournament-single/cash' },
  { title: 'Pontuação', icon: 'tournament-single/cash' },
  { title: 'Total de prêmio', icon: 'tournament-single/cash' },
  { title: 'Tipo de prêmio', icon: 'tournament-single/cash' },
];

const parsedCustomData = computed(() => {
  const desc = currentTournament.value?.description;

  if (!desc || typeof desc !== 'string' || desc.trim() === '') {
    return { descricoes: [], termos_condicoes: [] };
  }
  try {
    let sanitizedDescription = desc;

    sanitizedDescription = sanitizedDescription.replace(/(".*?")/gs, (match) =>
      match.replace(/[\r\n\u2028\u2029]/g, '\\n'),
    );
    sanitizedDescription = sanitizedDescription.replace(
      /[\r\n\u2028\u2029]/g,
      '',
    );
    // Only parse if it looks like a JSON array
    if (!sanitizedDescription.trim().startsWith('[')) {
      return { descricoes: [], termos_condicoes: [] };
    }

    const dataArray = JSON.parse(sanitizedDescription);
    const descricoes = dataArray.slice(0, 6);
    const termos_condicoes = dataArray.slice(6);

    return { descricoes, termos_condicoes };
  } catch (e) {
    console.error('Error parsing custom description:', e);

    return { descricoes: [], termos_condicoes: [] };
  }
});

async function triggerEnterInTournament() {
  if (isTournamentAutoRegister.value) {
    openToaster({
      message: t('gamification.tournament.automatic_registration_info'),
      type: 'info',
    });

    return;
  }

  if (currentTournament.value) {
    await enterInTournament(currentTournament.value.instance_id);
  }
}

const registeredPlayers = computed(() => {
  return currentTournament.value?.players || [];
});

const timer = reactive({
  days: '00',
  hours: '00',
  minutes: '00',
  seconds: '00',
});

function timeSince(date: number, date1: number) {
  const seconds = Math.floor((date1 - date) / 1000);
  let interval = seconds / 31536000;

  if (interval > 1) {
    return t('gamification.tournament.years', {
      value: Math.floor(interval),
    });
  }

  interval = seconds / 2592000;

  if (interval > 1) {
    return t('gamification.tournament.months', {
      value: Math.floor(interval),
    });
  }

  interval = seconds / 86400;

  if (interval > 1) {
    return t('gamification.tournament.days', {
      value: Math.floor(interval),
    });
  }

  interval = seconds / 3600;

  if (interval > 1) {
    return t('gamification.tournament.hours', {
      value: Math.floor(interval),
    });
  }

  interval = seconds / 60;

  if (interval > 1) {
    return t('gamification.tournament.minutes', {
      value: Math.floor(interval),
    });
  }

  return t('gamification.tournament.seconds', {
    value: Math.floor(seconds),
  });
}

function calcTimes() {
  let text = '';

  if (
    currentTournament.value?.is_finished &&
    currentTournament.value?.end_time
  ) {
    text =
      t('gamification.tournament.finished') +
      ' ' +
      timeSince(new Date().getTime(), currentTournament.value?.end_time);
  } else if (currentTournament.value?.is_in_progress) {
    text = t('gamification.tournament.ending_in');
    timeTo(new Date().getTime(), currentTournament.value?.end_time);
  } else if (
    currentTournament.value?.is_upcoming &&
    currentTournament.value?.start_time
  ) {
    text = t('gamification.tournament.starting_in');
    timeTo(new Date().getTime(), currentTournament.value?.start_time);
  } else if (
    currentTournament.value?.start_time &&
    currentTournament.value?.is_can_register
  ) {
    text = t('gamification.tournament.starts_in');
    timeTo(new Date().getTime(), currentTournament.value?.start_time);
  }

  return text;
}

function timeTo(date: number, date1: number) {
  let delta = Math.abs(date1 - date) / 1000;

  const days = Math.floor(delta / 86400);

  timer.days = days.toString().padStart(2, '0');
  delta -= days * 86400;

  const hours = Math.floor(delta / 3600) % 24;

  timer.hours = hours.toString().padStart(2, '0');
  delta -= hours * 3600;

  const minutes = Math.floor(delta / 60) % 60;

  timer.minutes = minutes.toString().padStart(2, '0');
  delta -= minutes * 60;

  const seconds = delta % 60;

  timer.seconds = seconds.toFixed(0).padStart(2, '0');
}

const isTournamentAutoRegister = computed(() => {
  return currentTournament.value?.registration_type === 'AUTO';
});

const activeTab = ref('ranking'); // 'ranking', 'prizes', 'terms'

const rankingTable = ref();

const rankingTableHeight = ref(0);

const updateTableHeight = () => {
  nextTick(() => {
    setTimeout(() => {
      if (rankingTable.value?.tableContainer) {
        rankingTableHeight.value =
          rankingTable.value.tableContainer.offsetHeight;
      }
    }, 50);
  });
};

const hasPrizes = computed(() => {
  return (
    currentTournament.value?.prizes && currentTournament.value.prizes.length > 0
  );
});

const hasTerms = computed(() => {
  return (
    parsedCustomData.value.termos_condicoes &&
    parsedCustomData.value.termos_condicoes.length > 0
  );
});

const { width } = useWindowSize();

const responsiveImage = computed(() => {
  if (width.value < 768) {
    return currentTournament.value?.image1;
  }

  return currentTournament.value?.image2;
});

const playerCountText = computed(() => {
  const count = currentTournament.value?.registration_count ?? '---';

  if (width.value < 1024) {
    return count;
  }

  return `${count} jogadores`;
});

const userPlayer = computed(() => {
  const me = currentTournament.value?.me;

  if (me && (me.position === null || me.position > 10)) {
    return {
      positionRange: me.position?.toString() || 'N/A',
      public_username: me.public_username,
      avatar_url: me.avatar_url,
      scores: me.scores,
      is_me: true,
    };
  }

  return null;
});

const isLoadingPage = ref(true);

const rankingColumns = [
  { text: 'Posição do jogador', align: 'left' },
  { text: 'Pontos', align: 'right' },
];

const displayedPlayers = computed(() => {
  if (!currentTournament.value?.prizes) return [];

  const prizes = currentTournament.value.prizes;
  const players = registeredPlayers.value;
  const result = [];
  let prizeIndex = 0;
  let fallbackIndex = 11; // começa do decimo segundo jogador

  while (prizeIndex < prizes.length) {
    const prize = prizes[prizeIndex];

    if (prize && prize.place_from === prize.place_to) {
      const pos = prize.place_from;
      const player = players.find((p: any) => p.position === pos);

      result.push({
        positionRange: `${pos}`,
        public_username: player?.public_username ?? '',
        avatar_url: player?.avatar_url ?? '',
        scores: player?.scores ?? '',
        is_me: player?.is_me ?? false,
      });
      prizeIndex++;
    } else if (prize && prize.place_from < prize.place_to) {
      // Range (apenas se for realmente um range)
      const from = Math.max(prize.place_from, 11);
      const to = prize.place_to;
      // Tenta pegar o jogador da posição 'from'
      let player = players.find((p: any) => p.position === from);
      // Se não existir, tenta o próximo disponível (from+1, from+2, ...)
      let nextPos = from;

      while (!player && nextPos <= to) {
        player = players.find((p: any) => p.position === nextPos);
        nextPos++;
      }
      // Se ainda não existir, usa fallback: segundo, terceiro, quarto...
      if (!player) {
        // fallbackIndex começa em 1 (segundo jogador)
        player =
          players[fallbackIndex] !== undefined
            ? players[fallbackIndex]
            : players[0] !== undefined
              ? players[0]
              : undefined;
        fallbackIndex++;
        // se não houver nenhum jogador, retorna vazio
        if (!player) {
          result.push({
            positionRange: `${from}-${to}`,
            public_username: '',
            avatar_url: '',
            scores: '',
            is_me: false,
          });
          prizeIndex++;
          continue;
        }
      }
      result.push({
        positionRange: `${from}-${to}`,
        public_username: player?.public_username ?? '',
        avatar_url: player?.avatar_url ?? '',
        scores: player?.scores ?? '',
        is_me: player?.is_me ?? false,
      });
      prizeIndex++;
    } else {
      prizeIndex++;
    }
  }

  // Remove linhas repetidas por posição
  const unique = [];
  const seen = new Set();

  for (const item of result) {
    if (!seen.has(item.positionRange)) {
      unique.push(item);
      seen.add(item.positionRange);
    }
  }

  return unique;
});

// Agrupa prêmios por posição
const groupedPrizes = computed(() => {
  if (!currentTournament.value?.prizes) return [];

  const prizes = currentTournament.value.prizes;

  const groups: Record<string, string[]> = {};

  prizes.forEach((prize) => {
    const key =
      prize.place_from === prize.place_to
        ? `${prize.place_from}`
        : `${prize.place_from}-${prize.place_to}`;

    if (!groups[key]) groups[key] = [];
    groups[key].push(prize.name);
  });

  return Object.entries(groups).map(([positionRange, names]) => ({
    positionRange,
    names,
  }));
});

// Função para determinar o tamanho da fonte baseado no comprimento do texto
const getPrizeFontClass = (text: string) => {
  const length = text.length;

  if (length <= 8) return 'text-base font-bold'; // Fonte maior

  if (length <= 12) return 'text-sm font-bold'; // Fonte normal

  if (length <= 18) return 'text-xs font-bold'; // Fonte pequena

  if (length <= 25) return 'text-[11px] font-bold'; // Fonte menor

  return 'text-[10px] font-bold'; // Fonte muito pequena
};

// Função para determinar o tamanho da fonte dos pontos
const getScoreFontClass = (score: number | string) => {
  const text = score.toString();
  const length = text.length;

  if (length <= 4) return 'text-base font-bold'; // Fonte maior para números pequenos

  if (length <= 6) return 'text-sm font-bold'; // Fonte normal

  if (length <= 8) return 'text-xs font-bold'; // Fonte pequena

  return 'text-[11px] font-bold'; // Fonte menor para números muito grandes
};

// Função para formatar números com separador de milhar
const formatScore = (score: number | string | null | undefined) => {
  if (score === null || score === undefined || score === '') return '-';
  try {
    return new Intl.NumberFormat('pt-BR').format(Number(score));
  } catch {
    return String(score);
  }
};

// Função para determinar o tamanho da fonte das posições dos jogadores (ranking)
const getPositionFontClass = (position: number) => {
  if (position <= 10) return 'text-sm font-bold pl-1';
  if (position <= 99) return 'text-xs font-bold pl-1';

  return 'text-[11px] font-bold';
};

// Função para determinar o tamanho da fonte das posições dos prêmios (range)
const getPrizePositionFontClass = (positionRange: string) => {
  // Exemplo: '1', '2-5', '10-20'
  const first = Number(positionRange.split('-')[0]);

  if (first <= 3) return 'text-base font-bold text-white';
  if (first <= 10) return 'text-sm font-bold text-white';
  if (first <= 99) return 'text-xs font-bold text-white';

  return 'text-[11px] font-bold text-white';
};

// Função para anonimizar nomes dos jogadores
const anonymizePlayerName = (name: string) => {
  if (!name || name.length <= 3) return name;

  const firstThree = name.substring(0, 3);

  return `${firstThree}****`;
};

const prizeColumns = [
  { text: 'Posição', align: 'left' },
  { text: 'Prêmio', align: 'right' },
];

const triggerLoadTournamentDetails = async () => {
  isLoadingPage.value = true;
  tournamentDetails.value = await loadTournamentInfo(
    Number.parseInt(params.tournament),
  );
  isLoadingPage.value = false;
  nextTick(() => updateTableHeight());
};

function getRowHeight(positionRange: string) {
  const prizeGroup = groupedPrizes.value.find(
    (group) => group.positionRange === positionRange,
  );

  let count = 1;

  if (prizeGroup && Array.isArray(prizeGroup.names)) {
    count = prizeGroup.names.length;
  }

  // Altura base 40px + 20px para cada prêmio extra
  return `h-[${40 + (count - 1) * 20}px]`;
}

let timerInterval: NodeJS.Timeout | null = null;

onMounted(async () => {
  timerInterval = setInterval(() => {
    calcTimes();
  }, 1000);

  await nextTick();
  updateTableHeight();
});

watch(activeTab, updateTableHeight);
watch(isDesktop, updateTableHeight);
watch(isLoadingPage, (newVal) => {
  if (!newVal) {
    nextTick(() => updateTableHeight());
  }
});

onBeforeMount(async () => {
  if (params.tournament) {
    await triggerLoadTournamentDetails();
  }
});

onUnmounted(() => {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
});
</script>

<template>
  <div
    v-if="currentTournament"
    class="w-full bg-[#020716] relative select-none -mt-3 lg:-mt-1"
  >
    <BaseScreenHeader
      heading-tag="h1"
      :on-back="handleBackNavigation"
      title="Torneios"
    />

    <div
      :class="{
        'flex flex-col justify-center items-center gap-4 self-stretch [background:rgba(255,255,255,0.05)] p-2.5 rounded-[10px]':
          !isDesktop,
      }"
    >
      <div
        class="relative lg:mb-4"
        :class="{
          grayscale:
            tournamentDetails?.is_finished || tournamentDetails?.is_cancelled,
        }"
      >
        <NuxtImg
          class="w-full rounded-[10px] lg:rounded-[20px] border border-solid border-[#FF450C]"
          draggable="false"
          :src="responsiveImage"
        />

        <div
          v-if="currentTournament.ribbon"
          class="absolute top-0 left-0"
        >
          <div
            class="flex justify-center items-center gap-2.5 [background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] px-[10px] py-[5px] lg:py-[10px] rounded-[10px_0] text-white text-[10px] lg:text-[12px] font-extrabold"
          >
            {{ 'TORNEIO ' + currentTournament.ribbon }}
          </div>
        </div>

        <div class="absolute bottom-0 right-0">
          <div
            class="flex justify-center items-center gap-[5px] [background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] px-[10px] py-[5px] lg:py-[10px] rounded-[10px_0] text-white text-[10px] lg:text-[12px] font-bold"
          >
            <NuxtIcon
              class="text-[12px]"
              name="players"
            />
            {{ playerCountText }}
          </div>
        </div>
      </div>

      <div
        v-if="isDesktop"
        class="flex justify-between items-center text-white font-semibold"
      >
        <div class="flex flex-col max-w-[38%]">
          <span class="overflow-ellipsis text-nowrap truncate">
            {{ currentTournament.name }}
          </span>
          <span class="overflow-ellipsis text-nowrap truncate">
            Prêmio Total
            <span class="text-[#00C9FF]">
              {{ currentTournament.prize_pool_short }}
            </span>
          </span>
        </div>
        <div
          class="flex justify-center items-center gap-[15px] [background:rgba(255,255,255,0.05)] p-2.5 rounded-[10px]"
        >
          <span
            v-if="!currentTournament.is_finished"
            class="w-[171px] px-2.5 flex items-center gap-2"
          >
            <NuxtIcon
              class="text-[20px]"
              name="timer"
            />
            <div class="flex items-center gap-1">
              <span class="flex flex-col items-center">
                <span class="leading-3">{{
                  timer.days !== '00' ? `${timer.days}` : `00`
                }}</span>
                <span
                  class="text-[rgba(255,255,255,0.50)] text-[8px] font-semibold leading-[10px]"
                >
                  {{ 'DIAS' }}
                </span>
              </span>
              {{ ' : ' }}
              <span class="flex flex-col items-center">
                <span class="leading-3">{{
                  timer.hours !== '00' ? `${timer.hours}` : `00`
                }}</span>
                <span
                  class="text-[rgba(255,255,255,0.50)] text-[8px] font-semibold leading-[10px]"
                >
                  {{ 'HORAS' }}
                </span>
              </span>
              {{ ' : ' }}
              <span class="flex flex-col items-center">
                <span class="leading-3">{{
                  timer.minutes !== '00' ? `${timer.minutes}` : `00`
                }}</span>
                <span
                  class="text-[rgba(255,255,255,0.50)] text-[8px] font-semibold leading-[10px]"
                >
                  {{ 'MINS' }}
                </span>
              </span>
              {{ ' : ' }}
              <span class="flex flex-col items-center">
                <span class="leading-3">{{
                  timer.seconds !== '00' ? `${timer.seconds}` : `00`
                }}</span>
                <span
                  class="text-[rgba(255,255,255,0.50)] text-[8px] font-semibold leading-[10px]"
                >
                  {{ 'SEGS' }}
                </span>
              </span>
            </div>
          </span>
          <div class="flex text-center">
            <button
              :class="[
                'flex h-[47px] justify-center items-center gap-[5px] lg:gap-[7px] border px-2.5 lg:px-5 py-0 rounded-md border-solid uppercase',
                {
                  '[background:radial-gradient(168.3%_138%_at_36.76%_-16.67%,#4AFF6B_0%,#00D420_100%)] border-[#84FC9C]':
                    currentTournament.is_user_registered &&
                    !currentTournament.is_finished,
                  '[background:radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF4A4A_0%,#D40000_100%)] border-[#FC8484]':
                    currentTournament.is_finished,
                  '[background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] border-[#FC9984]':
                    !currentTournament.is_user_registered &&
                    !currentTournament.is_finished &&
                    !currentTournament.is_cancelled,
                },
              ]"
              :disabled="
                currentTournament.is_user_registered ||
                currentTournament.is_finished ||
                currentTournament.is_cancelled
              "
              @click="triggerEnterInTournament"
            >
              <span class="text-[14px] font-extrabold leading-3">
                <template v-if="currentTournament.is_finished">
                  {{ $t('gamification.tournament.already_ended') }}
                </template>
                <template v-else-if="currentTournament.is_user_registered">
                  Você está participando
                </template>
                <template v-else-if="currentTournament.is_cancelled">
                  {{ $t('gamification.tournament.was_canceled') }}
                </template>
                <template v-else-if="isTournamentAutoRegister">
                  {{ $t('gamification.tournament.automatic_registration') }}
                </template>
                <template v-else>
                  {{ $t('gamification.tournament.join') }}
                </template>
              </span>
            </button>
          </div>
        </div>
      </div>
      <div
        v-else
        class="flex flex-col w-full items-center justify-center gap-4 text-white font-semibold"
      >
        <span class="line-clamp-2 text-center">
          {{ currentTournament.name }}
        </span>

        <span
          v-if="!currentTournament.is_finished"
          class="w-[171px] px-2.5 flex items-center gap-2"
        >
          <NuxtIcon
            class="text-[20px]"
            name="timer"
          />
          <div class="flex items-center gap-1">
            <span class="flex flex-col items-center">
              <span class="leading-3">{{
                timer.days !== '00' ? `${timer.days}` : `00`
              }}</span>
              <span
                class="text-[rgba(255,255,255,0.50)] text-[8px] font-semibold leading-[10px]"
              >
                {{ 'DIAS' }}
              </span>
            </span>
            {{ ' : ' }}
            <span class="flex flex-col items-center">
              <span class="leading-3">{{
                timer.hours !== '00' ? `${timer.hours}` : `00`
              }}</span>
              <span
                class="text-[rgba(255,255,255,0.50)] text-[8px] font-semibold leading-[10px]"
              >
                {{ 'HORAS' }}
              </span>
            </span>
            {{ ' : ' }}
            <span class="flex flex-col items-center">
              <span class="leading-3">{{
                timer.minutes !== '00' ? `${timer.minutes}` : `00`
              }}</span>
              <span
                class="text-[rgba(255,255,255,0.50)] text-[8px] font-semibold leading-[10px]"
              >
                {{ 'MINS' }}
              </span>
            </span>
            {{ ' : ' }}
            <span class="flex flex-col items-center">
              <span class="leading-3">{{
                timer.seconds !== '00' ? `${timer.seconds}` : `00`
              }}</span>
              <span
                class="text-[rgba(255,255,255,0.50)] text-[8px] font-semibold leading-[10px]"
              >
                {{ 'SEGS' }}
              </span>
            </span>
          </div>
        </span>

        <div
          class="flex flex-col justify-center items-center self-stretch [background:rgba(0,201,255,0.10)] backdrop-blur-[10px] px-4 py-[9px] rounded-[10px]"
        >
          <span
            class="text-white [font-family:'Plus_Jakarta_Sans'] text-[10px] font-semibold leading-4"
          >
            PRÊMIO TOTAL
          </span>
          <span
            class="text-[#00C9FF] text-center text-xl font-bold leading-5 tracking-[-0.8px]"
          >
            {{ currentTournament.prize_pool_short }}
          </span>
        </div>

        <button
          :class="[
            'flex h-[47px] w-full justify-center items-center gap-[5px] lg:gap-[7px] border px-2.5 lg:px-5 py-0 rounded-md border-solid uppercase',
            {
              '[background:radial-gradient(168.3%_138%_at_36.76%_-16.67%,#4AFF6B_0%,#00D420_100%)] border-[#84FC9C]':
                currentTournament.is_user_registered &&
                !currentTournament.is_finished,
              '[background:radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF4A4A_0%,#D40000_100%)] border-[#FC8484]':
                currentTournament.is_finished,
              '[background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] border-[#FC9984]':
                !currentTournament.is_user_registered &&
                !currentTournament.is_finished &&
                !currentTournament.is_cancelled,
            },
          ]"
          :disabled="
            currentTournament.is_user_registered ||
            currentTournament.is_finished ||
            currentTournament.is_cancelled
          "
          @click="triggerEnterInTournament"
        >
          <span class="text-[14px] font-extrabold leading-[15px]">
            <template v-if="currentTournament.is_finished">
              {{ $t('gamification.tournament.already_ended') }}
            </template>
            <template v-else-if="currentTournament.is_user_registered">
              Participando
            </template>
            <template v-else-if="currentTournament.is_cancelled">
              {{ $t('gamification.tournament.was_canceled') }}
            </template>
            <template v-else-if="isTournamentAutoRegister">
              {{ $t('gamification.tournament.automatic_registration') }}
            </template>
            <template v-else>
              {{ $t('gamification.tournament.join') }}
            </template>
          </span>
        </button>
      </div>

      <div
        class="grid grid-cols-2 lg:grid-cols-3 gap-2 lg:gap2.5 lg:mt-4 w-full"
      >
        <div
          v-for="(item, index) in descriptionItems"
          :key="`desc-${index}`"
          class="flex min-w-[160px] items-center gap-2.5 lg:gap-4 border px-2.5 py-2.5 lg:px-[20px] lg:py-[15px] rounded-[10px] border-solid border-[#1D2329]"
        >
          <NuxtIcon
            class="text-[20px] lg:text-[30px] shrink-0 aspect-[10/9] fill-[rgba(255,255,255,0.50)]"
            :filled="true"
            :name="item.icon"
          />
          <div class="flex flex-col">
            <span class="text-[11px] lg:text-sm font-semibold text-white">{{
              item.title
            }}</span>
            <p
              class="text-[11px] lg:text-base font-semibold leading-3 text-[#00C9FF]"
              >{{ parsedCustomData.descricoes[index] }}</p
            >
          </div>
        </div>
      </div>
    </div>

    <!-- JOGOS RELACIONADOS (SÓ APARECE NO MOBILE ACIMA DAS ABAS) -->
    <RelatedGames
      v-if="
        !isDesktop &&
        currentTournament?.related_games &&
        currentTournament.related_games.length > 0
      "
      icon-path="home/jogos-ao-vivo"
      :is-loading="isLoadingPage"
      :items="currentTournament.related_games"
      :per-row="[6, 4, 3]"
      :space-between="[24, 16, 12]"
      title="Jogos participantes"
    />

    <!-- NAVEGAÇÃO DAS ABAS (SÓ APARECE NO MOBILE) -->
    <div
      v-if="!isDesktop"
      class="flex items-center w-full mt-4 justify-between gap-3"
    >
      <button
        v-if="true"
        :class="[
          'tabsApostatudo__tab-button',
          { active: activeTab === 'ranking' },
        ]"
        :disabled="activeTab === 'ranking'"
        @click="activeTab = 'ranking'"
      >
        Classificação
      </button>
      <button
        v-if="hasPrizes"
        :class="[
          'tabsApostatudo__tab-button',
          { active: activeTab === 'prizes' },
        ]"
        :disabled="activeTab === 'prizes'"
        @click="activeTab = 'prizes'"
      >
        Prêmios
      </button>
      <button
        v-if="hasTerms"
        :class="[
          'tabsApostatudo__tab-button',
          { active: activeTab === 'terms' },
        ]"
        :disabled="activeTab === 'terms'"
        @click="activeTab = 'terms'"
      >
        T&C
      </button>
    </div>

    <!-- CONTAINER DO CONTEÚDO (GRID NO DESKTOP) -->
    <div
      class="w-full lg:flex lg:flex-wrap lg:justify-between lg:gap-4 mt-4 mb-4 lg:mb-0"
    >
      <!-- 1. CLASSIFICAÇÃO -->
      <TournamentTable
        v-if="activeTab === 'ranking' || isDesktop"
        ref="rankingTable"
        bg-color="rgba(0,201,255,0.20)"
        :columns="rankingColumns"
        :items="displayedPlayers"
        table-type="ranking"
        title="Classificação"
        :user-item="userPlayer"
      >
        <template #row-content="{ item: player, index, isUser }">
          <template v-if="isUser">
            <td
              class="text-center py-[10px] align-middle"
              colspan="2"
            >
              <div
                class="flex justify-between items-center flex-nowrap bg-[rgba(0,201,255,0.30)] border border-solid border-[#00C9FF] rounded-[8px] px-[10px] py-[8px]"
              >
                <span
                  class="font-semibold text-white whitespace-nowrap text-[14px]"
                >
                  SUA PONTUAÇÃO
                </span>
                <span
                  :class="[
                    'inline-block whitespace-nowrap text-[#00C9FF]',
                    getScoreFontClass(player.scores),
                  ]"
                  :title="player.scores ? player.scores.toString() : ''"
                >
                  {{ formatScore(player.scores) }}
                </span>
              </div>
            </td>
          </template>
          <template v-else>
            <td
              :class="[
                'text-left max-w-0 overflow-hidden px-[10px] py-[8px] align-middle',
                getRowHeight(player.positionRange),
                (index + 1) % 2 !== 0 ? 'bg-white/10' : '',
                'rounded-l-[7px]',
              ]"
            >
              <span class="flex items-center gap-2 font-bold text-white">
                <span
                  class="flex justify-start items-center w-[60px] min-w-[60px]"
                >
                  <template
                    v-if="
                      player.positionRange &&
                      ['1', '2', '3'].includes(player.positionRange)
                    "
                  >
                    <img
                      alt="Posição"
                      class="w-[22px] h-[22px] object-contain rounded-full -ml-0.5"
                      :src="
                        getAssetsUrlForCDN(
                          `/assets/gamification/t-place-${player.positionRange}.png`,
                        )
                      "
                    />
                  </template>
                  <template v-else>
                    <span
                      :class="
                        getPositionFontClass(
                          Number(player.positionRange.split('-')[0]),
                        )
                      "
                    >
                      {{ player.positionRange }}
                    </span>
                  </template>
                </span>
                <NuxtImg
                  v-if="player.avatar_url"
                  alt="Avatar"
                  class="w-[22px] h-[22px] object-contain rounded-full ml-2"
                  :src="player.avatar_url"
                />
                <span class="truncate ml-2">
                  {{
                    player.public_username
                      ? anonymizePlayerName(player.public_username)
                      : '*****'
                  }}
                </span>
              </span>
            </td>
            <td
              :class="[
                'text-right max-w-0 overflow-hidden px-[10px] py-[8px] align-middle',
                getRowHeight(player.positionRange),
                (index + 1) % 2 !== 0 ? 'bg-white/10' : '',
                'rounded-r-[7px]',
              ]"
            >
              <span
                :class="[
                  'inline-block whitespace-nowrap text-[#00C9FF]',
                  getScoreFontClass(player.scores),
                ]"
                :title="player.scores ? player.scores.toString() : ''"
              >
                {{ formatScore(player.scores) }}
              </span>
            </td>
          </template>
        </template>
      </TournamentTable>

      <!-- 2. PRÊMIOS -->
      <TournamentTable
        v-if="hasPrizes && (activeTab === 'prizes' || isDesktop)"
        bg-color="#121722"
        border-color="#1D2329"
        :columns="prizeColumns"
        :items="groupedPrizes"
        table-type="prizes"
        title="Prêmios"
        :user-item="userPlayer"
      >
        <template #row-content="{ item: prize, index, isUser }">
          <template v-if="isUser">
            <td
              class="text-center py-[10px] align-middle"
              colspan="2"
            >
              <div
                v-if="isDesktop"
                style="height: 39px"
              ></div>
            </td>
          </template>
          <template v-else>
            <td
              :class="[
                'text-left max-w-0 overflow-hidden px-[10px] py-[8px] align-middle',
                {
                  'bg-white/10 rounded-l-[7px]': (index + 1) % 2 !== 0,
                },
              ]"
            >
              <span
                :class="[
                  'font-bold',
                  getPrizePositionFontClass(prize?.positionRange ?? '-'),
                ]"
                ><!-- fonte condicional -->
                {{ prize?.positionRange ?? '-' }}
              </span>
            </td>
            <td
              :class="[
                'text-right px-[10px] py-[8px] align-middle h-[40px]',
                {
                  'bg-white/10 rounded-r-[7px]': (index + 1) % 2 !== 0,
                },
              ]"
            >
              <span
                v-for="(name, i) in prize.names"
                :key="i"
                :class="[
                  'text-[#00C9FF] truncate max-w-full block',
                  getPrizeFontClass(name),
                ]"
                :title="name"
              >
                {{ name }}
              </span>
            </td>
          </template>
        </template>
      </TournamentTable>

      <!-- 3. TERMOS E CONDIÇÕES -->
      <Terms
        v-if="hasTerms && (activeTab === 'terms' || isDesktop)"
        :parsed-custom-data="parsedCustomData"
        :table-height="rankingTableHeight"
      />
    </div>

    <!-- JOGOS RELACIONADOS (SÓ APARECE NO DESKTOP NO FINAL) -->
    <RelatedGames
      v-if="
        isDesktop &&
        currentTournament?.related_games &&
        currentTournament.related_games.length > 0
      "
      icon-path="home/jogos-ao-vivo"
      :is-loading="isLoadingPage"
      :items="currentTournament.related_games"
      :per-row="[6, 4, 3]"
      :space-between="[24, 16, 12]"
      title="Jogos participantes"
    />
  </div>

  <SmarticoAlertsAndLoader
    v-else
    :force-show-loading="true"
  />
</template>
<style scoped lang="postcss">
.tabsApostatudo__tab-button {
  @apply flex-1;
  padding: 7px 10px;
  height: 28px;
  border-radius: 6px;
  background: #08122f;
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #ffffff;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 0.3s ease,
    color 0.3s ease,
    border-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ffffff30;
}
.tabsApostatudo__tab-button:hover {
  background-color: #021629cd;
}
.tabsApostatudo__tab-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.tabsApostatudo__tab-button.active {
  height: 28px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(0, 201, 255, 0) 0%,
    rgba(0, 201, 255, 0.1) 100%
  );
  border: 1px solid #00c9ff;
  opacity: 1;
  vertical-align: middle;
  color: #00c9ff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.3s ease,
    border-color 0.3s ease,
    color 0.3s ease;
}
</style>
